// Store provides state that are pure ui manipulations

import produce, { enableMapSet } from "immer";
import makeStore from "../../../utils/makeStore";
import {
  IQuickPin,
  IRegistryLayoutStore,
  registryLayoutStoreInitialState,
} from "./models/RegistryLayoutStore";

enableMapSet();

export enum RegistryLayoutStoreActions {
  setHomeState = "setHomeState",
  addQuickPin = "addQuickPin",
  setRegistryId = "setRegistryId",
  removeQuickPinById = "removeQuickPinById",
  togglePaneOpen = "togglePaneOpen",
  toggleGeneMapView = "toggleGeneMapView",
  toggleCaseView = "toggleCaseView",
  setIsEditMode = "setIsEditMode",
  setIsAddMode = "setIsAddMode",
  setDateGeneMap = "setDateGeneMap",
  setTypeGeneMap = "setTypeGeneMap",
  addHiddenColumn = "addHiddenColumn",
  removeHiddenColumn = "removeHiddenColumn",
}

export interface IRegistryLayoutStoreAction {
  type: RegistryLayoutStoreActions;
  payload:
    | IAddQuickPinAction
    | ISetRegistryIdAction
    | IRemoveQuickPinByIdAction
    | ISetIsEditModeAction
    | ISetIsAddModeAction
    | ISetDateGeneMapAction
    | ISetTypeGeneMapAction
    | IAddHiddenColumn
    | IRemoveHiddenColumn;
}

const setHomeState = (draft: IRegistryLayoutStore) => {
  draft.isAddMode = false;
  draft.isCaseView = false;
  draft.isPaneOpen = false;
  draft.isEditMode = false;
  draft.isGeneMapView = false;
};

export interface IAddQuickPinAction {
  quickPin: IQuickPin;
}
const addQuickPin = (
  draft: IRegistryLayoutStore,
  payload: IAddQuickPinAction
) => {
  if (!draft.quickPins) {
    draft.quickPins = {};
    draft.quickPins[payload.quickPin.targetPath] = payload.quickPin;
  }

  draft.quickPins[payload.quickPin.targetPath] = payload.quickPin;
};

export interface IRemoveQuickPinByIdAction {
  quickPinPath: IQuickPin["targetPath"];
}
const removeQuickPinById = (
  draft: IRegistryLayoutStore,
  payload: IRemoveQuickPinByIdAction
) => {
  if (draft.quickPins) {
    delete draft.quickPins[payload.quickPinPath];
  }
};

export interface ISetRegistryIdAction {
  registryId: IRegistryLayoutStore["registryId"];
}
const setRegistryId = (
  draft: IRegistryLayoutStore,
  payload: ISetRegistryIdAction
) => {
  draft.registryId = payload.registryId;
};

export interface ISetIsEditModeAction {
  isEditMode: IRegistryLayoutStore["isEditMode"];
}
const setIsEditMode = (
  draft: IRegistryLayoutStore,
  payload: ISetIsEditModeAction
) => {
  draft.isEditMode = payload.isEditMode;

  // Set layouts off
  draft.isGeneMapView = false;
  draft.isCaseView = false;
  draft.isPaneOpen = false;
};

export interface ISetDateGeneMapAction {
  dateGeneMap: IRegistryLayoutStore["dateGeneMap"];
}
const setDateGeneMap = (
  draft: IRegistryLayoutStore,
  payload: ISetDateGeneMapAction
) => {
  draft.dateGeneMap = payload.dateGeneMap;
};

export interface ISetTypeGeneMapAction {
  typeGeneMap: IRegistryLayoutStore["typeGeneMap"];
}
const setTypeGeneMap = (
  draft: IRegistryLayoutStore,
  payload: ISetTypeGeneMapAction
) => {
  draft.typeGeneMap = payload.typeGeneMap;
};

export interface IAddHiddenColumn {
  columnName: string;
}
const addHiddenColumn = (
  draft: IRegistryLayoutStore,
  payload: IAddHiddenColumn
) => {
  if (draft.hiddenColumns) {
    draft.hiddenColumns.push(payload.columnName);
  } else {
    draft.hiddenColumns = [payload.columnName];
  }
};
export interface IRemoveHiddenColumn {
  columnName: string;
}
const removeHiddenColumn = (
  draft: IRegistryLayoutStore,
  payload: IRemoveHiddenColumn
) => {
  if (draft.hiddenColumns) {
    draft.hiddenColumns = draft.hiddenColumns.filter(
      (item) => item !== payload.columnName
    );
  } else {
    draft.hiddenColumns = [];
  }
};
export interface ISetIsAddModeAction {
  isAddMode: IRegistryLayoutStore["isAddMode"];
}
const setIsAddMode = (
  draft: IRegistryLayoutStore,
  payload: ISetIsAddModeAction
) => {
  draft.isAddMode = payload.isAddMode;

  // Set layouts off
  draft.isGeneMapView = false;
  draft.isCaseView = false;
  draft.isPaneOpen = false;
};

const togglePaneOpen = (draft: IRegistryLayoutStore) => {
  draft.isPaneOpen = !draft.isPaneOpen;
};
const toggleGeneMapView = (draft: IRegistryLayoutStore) => {
  draft.isGeneMapView = !draft.isGeneMapView;
  draft.isCaseView = false;
};
const toggleCaseView = (draft: IRegistryLayoutStore) => {
  draft.isCaseView = !draft.isCaseView;
  draft.isGeneMapView = false;
};

const registryLayoutStoreReducer = (
  state: IRegistryLayoutStore,
  action: IRegistryLayoutStoreAction
) =>
  produce(state, (draft) => {
    switch (action.type) {
      case RegistryLayoutStoreActions.addQuickPin:
        addQuickPin(draft, action.payload as IAddQuickPinAction);
        break;

      case RegistryLayoutStoreActions.setHomeState:
        setHomeState(draft);
        break;

      case RegistryLayoutStoreActions.removeQuickPinById:
        removeQuickPinById(draft, action.payload as IRemoveQuickPinByIdAction);
        break;

      case RegistryLayoutStoreActions.setRegistryId:
        setRegistryId(draft, action.payload as ISetRegistryIdAction);
        break;

      case RegistryLayoutStoreActions.togglePaneOpen:
        togglePaneOpen(draft);
        break;

      case RegistryLayoutStoreActions.setIsEditMode:
        setIsEditMode(draft, action.payload as ISetIsEditModeAction);
        break;

      case RegistryLayoutStoreActions.addHiddenColumn:
        addHiddenColumn(draft, action.payload as IAddHiddenColumn);
        break;

      case RegistryLayoutStoreActions.removeHiddenColumn:
        removeHiddenColumn(draft, action.payload as IRemoveHiddenColumn);
        break;

      case RegistryLayoutStoreActions.setIsAddMode:
        setIsAddMode(draft, action.payload as ISetIsAddModeAction);
        break;

      case RegistryLayoutStoreActions.setDateGeneMap:
        setDateGeneMap(draft, action.payload as ISetDateGeneMapAction);
        break;

      case RegistryLayoutStoreActions.setTypeGeneMap:
        setTypeGeneMap(draft, action.payload as ISetTypeGeneMapAction);
        break;

      case RegistryLayoutStoreActions.toggleGeneMapView:
        toggleGeneMapView(draft);
        break;

      case RegistryLayoutStoreActions.toggleCaseView:
        toggleCaseView(draft);
        break;

      default:
        break;
    }
  });

const [
  RegistryLayoutStoreProvider,
  useRegistryLayoutStore,
  useRegistryLayoutDispatch,
] = makeStore(
  registryLayoutStoreReducer,
  registryLayoutStoreInitialState,
  "geneial00RegistryLayout",
  true
);

export {
  RegistryLayoutStoreProvider,
  useRegistryLayoutStore,
  useRegistryLayoutDispatch,
};
